import { FOOTER_PREMIUM } from "../common/footer";
import { PULSE_HEADER } from "../common/header";

/**
 * World-Class Billing Report Template
 * Features: Invoice card, charges breakdown, payment history, action buttons
 */
export const billingReport = `
<mjml>
  <mj-head>
    <mj-title>Billing Summary - {{billing_period}}</mj-title>
    <mj-preview>Your billing summary and payment details for {{billing_period}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="'Segoe UI', Arial, sans-serif" />
      <mj-text font-size="16px" color="#334155" line-height="1.6" />
      <mj-button font-family="'Segoe UI', Arial, sans-serif" font-size="16px" font-weight="600" />
    </mj-attributes>
    <mj-style>
      .status-paid { background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 6px 16px; border-radius: 20px; font-size: 12px; font-weight: 700; }
      .status-pending { background: linear-gradient(135deg, #F59E0B, #D97706); color: white; padding: 6px 16px; border-radius: 20px; font-size: 12px; font-weight: 700; }
      .status-overdue { background: linear-gradient(135deg, #EF4444, #DC2626); color: white; padding: 6px 16px; border-radius: 20px; font-size: 12px; font-weight: 700; }
    </mj-style>
  </mj-head>
  <mj-body background-color="#f1f5f9">
    \${PULSE_HEADER("Billing Summary", "{{billing_period}}")}

    <!-- Introduction -->
    <mj-section background-color="#ffffff" padding="35px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-text font-size="24px" color="#0f172a" font-weight="700" padding-bottom="12px">
          Hello {{first_name}},
        </mj-text>
        <mj-text font-size="15px" color="#64748b">
          Here's your billing summary for {{product_name}}. This report includes your invoice details, payment history, and upcoming charges.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Invoice Card Header -->
    <mj-section background-color="#1939B7" padding="25px 30px" border-radius="16px 16px 0 0">
      <mj-column width="60%">
        <mj-text font-size="14px" color="rgba(255,255,255,0.8)" text-transform="uppercase" letter-spacing="1px" padding-bottom="5px">
          Current Invoice
        </mj-text>
        <mj-text font-size="22px" color="#ffffff" font-weight="700">
          #{{invoice_number}}
        </mj-text>
      </mj-column>
      <mj-column width="40%">
        <mj-text align="right" padding-top="15px">
          <span class="{{payment_status_class}}">{{payment_status}}</span>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Invoice Details -->
    <mj-section background-color="#ffffff" padding="20px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-table>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 15px 0; color: #64748b; font-size: 14px;">Invoice Date</td>
            <td style="padding: 15px 0; text-align: right; font-weight: 600; color: #0f172a;">{{invoice_date}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 15px 0; color: #64748b; font-size: 14px;">Due Date</td>
            <td style="padding: 15px 0; text-align: right; font-weight: 600; color: #0f172a;">{{due_date}}</td>
          </tr>
          <tr>
            <td style="padding: 15px 0; color: #64748b; font-size: 14px;">Billing Period</td>
            <td style="padding: 15px 0; text-align: right; font-weight: 600; color: #0f172a;">{{billing_start}} - {{billing_end}}</td>
          </tr>
        </mj-table>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Charges Breakdown -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Invoice Details
        </mj-text>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="20px">
          Charges Breakdown
        </mj-text>
        <mj-table>
          <tr style="border-bottom: 2px solid #e2e8f0; background-color: #f8fafc;">
            <th style="padding: 12px; text-align: left; font-size: 13px; color: #64748b; text-transform: uppercase;">Description</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Qty</th>
            <th style="padding: 12px; text-align: right; font-size: 13px; color: #64748b; text-transform: uppercase;">Amount</th>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; color: #334155;">{{plan_name}} Subscription</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; color: #64748b;">1</td>
            <td style="padding: 12px; text-align: right; font-size: 14px; font-weight: 600; color: #0f172a;">{{plan_amount}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; color: #334155;">Additional Messages</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; color: #64748b;">{{additional_messages_qty}}</td>
            <td style="padding: 12px; text-align: right; font-size: 14px; font-weight: 600; color: #0f172a;">{{additional_messages_amount}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; color: #334155;">AI Credits</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; color: #64748b;">{{additional_credits_qty}}</td>
            <td style="padding: 12px; text-align: right; font-size: 14px; font-weight: 600; color: #0f172a;">{{additional_credits_amount}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; color: #334155;" colspan="2">Subtotal</td>
            <td style="padding: 12px; text-align: right; font-size: 14px; font-weight: 600; color: #0f172a;">{{subtotal}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; color: #334155;" colspan="2">Tax ({{tax_rate}})</td>
            <td style="padding: 12px; text-align: right; font-size: 14px; font-weight: 600; color: #0f172a;">{{tax_amount}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; color: #10B981;" colspan="2">Discount</td>
            <td style="padding: 12px; text-align: right; font-size: 14px; font-weight: 600; color: #10B981;">-{{discount_amount}}</td>
          </tr>
          <tr style="background-color: #f0f9ff;">
            <td style="padding: 15px 12px; font-size: 16px; font-weight: 700; color: #0f172a;" colspan="2">Total Due</td>
            <td style="padding: 15px 12px; text-align: right; font-size: 20px; font-weight: 700; color: #1939B7;">{{total_amount}}</td>
          </tr>
        </mj-table>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Payment Info -->
    <mj-section background-color="#ffffff" padding="25px 30px" border-radius="16px">
      <mj-column width="48%" padding="10px" background-color="#f8fafc" border-radius="12px">
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Payment Method
        </mj-text>
        <mj-text font-size="15px" color="#0f172a" font-weight="600">
          {{payment_method_type}}
        </mj-text>
        <mj-text font-size="14px" color="#64748b">
          {{payment_method_details}}
        </mj-text>
      </mj-column>
      <mj-column width="48%" padding="10px" background-color="#f8fafc" border-radius="12px">
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Billing Address
        </mj-text>
        <mj-text font-size="15px" color="#0f172a" font-weight="600">
          {{billing_name}}
        </mj-text>
        <mj-text font-size="14px" color="#64748b">
          {{billing_address}}<br>{{billing_city_state_zip}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Payment History -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          History
        </mj-text>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="20px">
          Recent Payments
        </mj-text>
        <mj-table>
          <tr style="border-bottom: 2px solid #e2e8f0; background-color: #f8fafc;">
            <th style="padding: 12px; text-align: left; font-size: 13px; color: #64748b;">Date</th>
            <th style="padding: 12px; text-align: left; font-size: 13px; color: #64748b;">Invoice</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b;">Amount</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b;">Status</th>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px;">{{history_1_date}}</td>
            <td style="padding: 12px; font-size: 14px;">{{history_1_invoice}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600;">{{history_1_amount}}</td>
            <td style="padding: 12px; text-align: center;"><span style="color: #10B981; font-weight: 600;">{{history_1_status}}</span></td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px;">{{history_2_date}}</td>
            <td style="padding: 12px; font-size: 14px;">{{history_2_invoice}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600;">{{history_2_amount}}</td>
            <td style="padding: 12px; text-align: center;"><span style="color: #10B981; font-weight: 600;">{{history_2_status}}</span></td>
          </tr>
          <tr>
            <td style="padding: 12px; font-size: 14px;">{{history_3_date}}</td>
            <td style="padding: 12px; font-size: 14px;">{{history_3_invoice}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600;">{{history_3_amount}}</td>
            <td style="padding: 12px; text-align: center;"><span style="color: #10B981; font-weight: 600;">{{history_3_status}}</span></td>
          </tr>
        </mj-table>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Action Buttons -->
    <mj-section background-color="#ffffff" padding="25px 30px" border-radius="16px">
      <mj-column width="32%" padding="5px">
        <mj-button background-color="#1939B7" color="#ffffff" href="{{pay_now_url}}" border-radius="8px" font-weight="600" width="100%" font-size="14px">
          Pay Now
        </mj-button>
      </mj-column>
      <mj-column width="32%" padding="5px">
        <mj-button background-color="#1E293B" color="#ffffff" href="{{download_invoice_url}}" border-radius="8px" font-weight="600" width="100%" font-size="14px">
          Download PDF
        </mj-button>
      </mj-column>
      <mj-column width="32%" padding="5px">
        <mj-button background-color="#64748B" color="#ffffff" href="{{billing_settings_url}}" border-radius="8px" font-weight="600" width="100%" font-size="14px">
          Settings
        </mj-button>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Support Section -->
    <mj-section background-color="#f8fafc" padding="20px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="14px" color="#64748b" align="center">
          Questions about your bill? <a href="mailto:billing@example.com" style="color: #1939B7; text-decoration: none; font-weight: 600;">Contact our billing team</a>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="10px 0"></mj-section>

    \${FOOTER_PREMIUM}
  </mj-body>
</mjml>
`;

export default {
  id: "billingReport",
  name: "Billing Summary - Premium",
  category: "pulse",
  purpose: "pulse" as const,
  description:
    "A world-class billing report with gradient invoice header, detailed charges breakdown, payment info cards, payment history table, and action buttons.",
  features: [
    "Gradient invoice header card",
    "Detailed charges breakdown table",
    "Payment method and billing cards",
    "Payment history with status",
    "Quick action buttons",
    "Support contact footer",
  ],
  subject: "&#128176; Billing Summary - {{billing_period}}",
  subTitle: "Your billing summary and payment details",
  mjmlTemplate: billingReport,
  tags: ["pulse", "billing", "invoice", "payment", "report", "subscription"],
};
